// TB-122 (Karim 2026-10-22): the admin picks the display currency (BHD or USD).
// Amounts stay stored in BHD; USD is shown at the peg. Read once per session
// (one shared request), refreshed on login; BHD when the call fails.
// TB-123 (W12): no compiled rate. USD is only shown when the setting carries
// a valid rate; otherwise amounts stay in BHD.
import { reactive } from 'vue'
import axios from 'axios'
import { loadLocale } from '@/utils/locale'

// Amounts are stored in this currency.
export const BASE_CURRENCY = 'BHD'

const DEFAULT = { currency: BASE_CURRENCY, usdPerBhd: null, bhdPerUsd: null }

export const displayCurrency = reactive({ ...DEFAULT, loaded: false })

let pending = null

function apply (d) {
  let usdPerBhd = Number(d && d.usdPerBhd)
  let bhdPerUsd = Number(d && d.bhdPerUsd)
  if (!(bhdPerUsd > 0) && usdPerBhd > 0) bhdPerUsd = 1 / usdPerBhd
  if (!(usdPerBhd > 0) && bhdPerUsd > 0) usdPerBhd = 1 / bhdPerUsd
  const hasRate = bhdPerUsd > 0
  const usd = String((d && d.currency) || '').toUpperCase() === 'USD'
  displayCurrency.currency = usd && hasRate ? 'USD' : 'BHD'
  displayCurrency.usdPerBhd = hasRate ? usdPerBhd : null
  displayCurrency.bhdPerUsd = hasRate ? bhdPerUsd : null
}

/** Fetches the setting once; `force` re-reads it (after login). */
export function loadDisplayCurrency ({ force = false } = {}) {
  loadLocale({ force }) // TB-123: the locale setting loads at the same moments
  if (pending && !force) return pending
  const base = process.env.VUE_APP_TRANSACTIONS_SERVICE_API || ''
  pending = axios.get(`${base}settings/display-currency`)
    .then((r) => apply(r && r.data && r.data.data))
    .catch(() => { if (!displayCurrency.loaded) apply(DEFAULT) })
    .then(() => { displayCurrency.loaded = true; return displayCurrency })
  return pending
}

export const isUsd = () => displayCurrency.currency === 'USD'

/** Label for fee inputs: "BHD" or "USD". */
export const currencyLabel = () => (isUsd() ? 'USD' : 'BHD')

const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100
const r3 = (n) => Math.round((n + Number.EPSILON) * 1000) / 1000

// bhd / bhdPerUsd, as the server and the Stripe charge do (12 BHD -> $31.91)
export function bhdToUsd (bhd) {
  const n = Number(bhd)
  const rate = displayCurrency.bhdPerUsd
  return rate > 0 ? (Number.isFinite(n) ? n : 0) / rate : NaN
}

/** Stored BHD -> the number shown / edited in the display currency. */
export function toDisplayValue (bhd) {
  const n = Number(bhd)
  if (!Number.isFinite(n)) return bhd
  return isUsd() ? r2(bhdToUsd(n)) : n
}

/** A number typed in the display currency -> BHD to save (3 decimals). */
export function toBhdValue (value) {
  if (value === null || value === undefined || value === '') return value
  const n = Number(value)
  if (!Number.isFinite(n)) return value
  return isUsd() ? r3(n * displayCurrency.bhdPerUsd) : n
}
